import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X, Camera, ImageUp, FileText, Sparkles, AlertTriangle, CheckCircle2, RotateCcw, Edit3, ArrowRight, Trash2,
} from "lucide-react";
import {
  extractRecipeFromTextLocal, extractRecipeFromImageWithAI,
  buildRecipeDraftFromExtraction, validateExtractedRecipe,
} from "../utils/recipeExtractionUtils";

/* ════════════════════════════════════════════════════════════════════════
   RECIPE PHOTO IMPORT MODAL
   Take/upload a recipe photo (or paste text), parse to a recipe draft,
   show an editable review screen, then APPLY to RecipeEditModal. Never
   auto-saves; the user reviews + edits before final Save.
   ════════════════════════════════════════════════════════════════════════ */

export function RecipePhotoImportModal({ open, onClose, onApply }) {
  const [imageUrl, setImageUrl]   = useState("");      // data URL preview
  const [rawText,  setRawText]    = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extraction, setExtraction] = useState(null);  // parsed object
  const [useAsRecipePhoto, setUseAsRecipePhoto] = useState(true);
  const [aiAvailable, setAiAvailable] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setImageUrl(""); setRawText(""); setExtraction(null);
    setExtracting(false); setUseAsRecipePhoto(true); setAiAvailable(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImageUrl(String(reader.result || ""));
    reader.readAsDataURL(file);
    // Try AI extraction; falls back to manual-paste flow.
    setExtracting(true);
    try {
      const result = await extractRecipeFromImageWithAI(file);
      setAiAvailable(result.ok);
      if (result.ok && result.extraction) {
        setExtraction(result.extraction);
        toast.success("Recipe drafted — please review.");
      }
    } finally {
      setExtracting(false);
    }
  };

  const runTextExtract = () => {
    if (!rawText.trim()) { toast("Paste or type the recipe text first."); return; }
    const result = extractRecipeFromTextLocal(rawText);
    setExtraction(result);
    if ((result.confidence || "Low") === "Low") {
      toast("Recipe drafted — confidence is low, please review carefully.");
    } else {
      toast.success(`Recipe drafted (${result.confidence} confidence).`);
    }
  };

  const apply = () => {
    if (!extraction) return;
    const v = validateExtractedRecipe(extraction);
    if (!v.ok) toast(`Heads up: ${v.issues.join(", ")}`);
    const draft = buildRecipeDraftFromExtraction(extraction, useAsRecipePhoto ? imageUrl : "");
    onApply?.(draft, { referenceImage: useAsRecipePhoto ? "" : imageUrl });
    toast.success("Filled the recipe card — review and save when ready.");
    onClose?.();
  };

  if (!open) return null;

  const lowConfidence = extraction && (extraction.confidence === "Low");

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-950/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="recipe-photo-import-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl rounded-3xl bg-white dark:bg-slate-900 shadow-none border border-[color:var(--ara-line-soft)] dark:border-rose-900/40 overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 border-b border-[color:var(--ara-line-soft)] dark:border-rose-900/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[color:var(--ara-surface-alt)] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-[color:var(--ara-ink)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50 leading-tight">Import recipe from photo</h2>
              <p className="text-xs font-bold text-gray-500 mt-0.5">Take or upload a recipe photo and ARA will draft the recipe card — you stay in control.</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl hover:bg-white/60 dark:hover:bg-slate-800 flex items-center justify-center text-gray-400"
              aria-label="Close"
              data-testid="recipe-photo-import-close"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-3">
          {/* Source: image upload + paste text */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <Camera className="w-3 h-3" strokeWidth={3} /> Recipe image
              </p>
              <input
                ref={fileRef} type="file" accept="image/*,application/pdf"
                onChange={onPickImage} className="hidden"
                data-testid="recipe-photo-file-input"
              />
              {imageUrl ? (
                <div className="relative rounded-2xl border-2 border-[color:var(--ara-line-soft)] dark:border-rose-800 overflow-hidden">
                  <img src={imageUrl} alt="Recipe source" className="w-full h-44 object-cover" />
                  <button
                    type="button"
                    onClick={() => { setImageUrl(""); fileRef.current && (fileRef.current.value = ""); }}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/90 text-[color:var(--ara-ink-muted)] hover:bg-white shadow flex items-center justify-center"
                    aria-label="Remove image"
                    data-testid="recipe-photo-remove"
                  ><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full h-44 rounded-2xl border-2 border-dashed border-[color:var(--ara-line-soft)] dark:border-rose-800 bg-transparent dark:bg-transparent flex flex-col items-center justify-center gap-1 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] hover:bg-transparent"
                  data-testid="recipe-photo-pick"
                >
                  <ImageUp className="w-6 h-6" strokeWidth={1.5} />
                  <p className="text-xs font-extrabold">Take or upload a photo</p>
                  <p className="text-[10px] font-bold text-gray-500">JPG · PNG · HEIC · PDF</p>
                </button>
              )}
              {!aiAvailable && (
                <p className="text-[10px] italic text-gray-500 leading-snug" data-testid="recipe-photo-ai-notice">
                  AI recipe extraction requires a secure vision/OCR connection. For now, upload the image and paste / edit the recipe text below.
                </p>
              )}
              {extracting && (
                <p className="text-[10px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Reading image…</p>
              )}
            </div>

            <div className="space-y-1.5">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <FileText className="w-3 h-3" strokeWidth={3} /> Or paste recipe text
              </p>
              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                rows={8}
                placeholder={"Paste the recipe (title, ingredients, method)…\n\nTitle\n\nIngredients:\n- 2 cups flour\n- 1 cup milk\n\nMethod:\n1. Mix together"}
                className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-medium text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-300"
                data-testid="recipe-photo-text-input"
              />
              <button
                type="button"
                onClick={runTextExtract}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-2xl bg-[color:var(--ara-ink)] hover:opacity-85 text-[color:var(--ara-surface)] text-sm font-extrabold shadow-sm active:scale-[0.98]"
                data-testid="recipe-photo-extract-btn"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Draft recipe from text
              </button>
            </div>
          </div>

          {/* Safety copy */}
          <div className="rounded-xl bg-transparent dark:bg-transparent border border-[color:var(--ara-line-soft)] dark:border-amber-800 px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <p className="text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] leading-snug">
              <span className="uppercase tracking-wider font-extrabold">Estimate only.</span> ARA drafts the recipe from what it can read. Handwritten recipes may need more review — please check ingredients and method before saving. No medical or dietary advice.
            </p>
          </div>

          {/* Review screen */}
          {extraction && (
            <ExtractionReview
              extraction={extraction}
              onChange={setExtraction}
              lowConfidence={lowConfidence}
              useAsRecipePhoto={useAsRecipePhoto}
              setUseAsRecipePhoto={setUseAsRecipePhoto}
              hasImage={!!imageUrl}
            />
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 dark:border-slate-800 flex flex-col sm:flex-row gap-2 sm:justify-between">
          <button
            type="button"
            onClick={() => { setExtraction(null); setRawText(""); }}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-extrabold text-gray-700 dark:text-gray-200"
            data-testid="recipe-photo-try-again"
          >
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} /> Try again
          </button>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center px-4 py-2 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm font-extrabold text-gray-700 dark:text-gray-200"
              data-testid="recipe-photo-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={!extraction}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-2xl bg-[color:var(--ara-ink)] hover:opacity-85 disabled:opacity-40 disabled:cursor-not-allowed text-[color:var(--ara-surface)] text-sm font-extrabold shadow-sm active:scale-[0.98]"
              data-testid="recipe-photo-apply"
            >
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} /> Review recipe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Editable review of the extraction before "Apply" ────────────────── */

function ExtractionReview({ extraction, onChange, lowConfidence, useAsRecipePhoto, setUseAsRecipePhoto, hasImage }) {
  const set = (patch) => onChange({ ...extraction, ...patch });
  const setIng = (idx, val) => set({ ingredients: extraction.ingredients.map((x, i) => i === idx ? val : x) });
  const addIng = () => set({ ingredients: [...(extraction.ingredients || []), ""] });
  const delIng = (idx) => set({ ingredients: extraction.ingredients.filter((_, i) => i !== idx) });
  const setStep = (idx, val) => set({ instructions: extraction.instructions.map((x, i) => i === idx ? val : x) });
  const addStep = () => set({ instructions: [...(extraction.instructions || []), ""] });
  const delStep = (idx) => set({ instructions: extraction.instructions.filter((_, i) => i !== idx) });

  const confTone = extraction.confidence === "Good"
    ? "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-[color:var(--ara-ink)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-emerald-800"
    : extraction.confidence === "Medium"
      ? "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-[color:var(--ara-ink)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-amber-800"
      : "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-[color:var(--ara-ink)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-rose-800";

  return (
    <div className="rounded-2xl border border-[color:var(--ara-line-soft)] dark:border-violet-800 bg-transparent dark:bg-transparent p-3 space-y-2.5" data-testid="recipe-photo-review">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] flex items-center gap-1">
          <Sparkles className="w-3 h-3" strokeWidth={3} /> ARA found this recipe · please review
        </p>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${confTone}`} data-testid="recipe-photo-confidence">
          Match quality: {extraction.confidence || "Low"}
        </span>
      </div>

      {lowConfidence && (
        <p className="text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" data-testid="recipe-photo-low-warning">
          Please review carefully — some handwriting / formatting was unclear.
        </p>
      )}

      {extraction.needsReview?.length > 0 && (
        <div className="rounded-lg bg-transparent dark:bg-transparent border border-[color:var(--ara-line-soft)] dark:border-amber-800 px-2 py-1.5" data-testid="recipe-photo-needs-review">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" strokeWidth={3} /> Needs review
          </p>
          <ul className="mt-0.5 ml-3 list-disc text-[11px] text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">
            {extraction.needsReview.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      <Field label="Recipe title">
        <input value={extraction.title || ""} onChange={(e) => set({ title: e.target.value })} className={INPUT_CLS} data-testid="recipe-photo-field-title" />
      </Field>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Category">
          <input value={extraction.category || ""} onChange={(e) => set({ category: e.target.value })} className={INPUT_CLS} data-testid="recipe-photo-field-category" />
        </Field>
        <Field label="Servings">
          <input type="number" min="1" value={extraction.servings || ""} onChange={(e) => set({ servings: e.target.value })} className={INPUT_CLS} data-testid="recipe-photo-field-serves" />
        </Field>
        <Field label="Prep min">
          <input type="number" min="0" value={extraction.prepTime || ""} onChange={(e) => set({ prepTime: e.target.value })} className={INPUT_CLS} data-testid="recipe-photo-field-prep" />
        </Field>
      </div>

      <Field label="Ingredients">
        <div className="space-y-1">
          {(extraction.ingredients || []).map((ing, i) => (
            <div key={i} className="flex gap-1.5">
              <input value={ing} onChange={(e) => setIng(i, e.target.value)} className={INPUT_CLS} data-testid={`recipe-photo-ing-${i}`} />
              <button type="button" onClick={() => delIng(i)} className="px-2 rounded-xl bg-transparent hover:bg-transparent text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)]" aria-label="Remove ingredient"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
            </div>
          ))}
          <button type="button" onClick={addIng} className="text-[11px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" data-testid="recipe-photo-add-ing">+ Add ingredient</button>
        </div>
      </Field>

      <Field label="Method">
        <div className="space-y-1">
          {(extraction.instructions || []).map((step, i) => (
            <div key={i} className="flex gap-1.5">
              <input value={step} onChange={(e) => setStep(i, e.target.value)} className={INPUT_CLS} data-testid={`recipe-photo-step-${i}`} />
              <button type="button" onClick={() => delStep(i)} className="px-2 rounded-xl bg-transparent hover:bg-transparent text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)]" aria-label="Remove step"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
            </div>
          ))}
          <button type="button" onClick={addStep} className="text-[11px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" data-testid="recipe-photo-add-step">+ Add step</button>
        </div>
      </Field>

      <Field label="Notes">
        <textarea value={extraction.notes || ""} onChange={(e) => set({ notes: e.target.value })} rows={2} className={INPUT_CLS} data-testid="recipe-photo-field-notes" />
      </Field>

      {hasImage && (
        <div className="rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 px-2.5 py-2 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />
          <label className="flex items-center gap-2 text-[11px] font-bold text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={useAsRecipePhoto} onChange={(e) => setUseAsRecipePhoto(e.target.checked)} data-testid="recipe-photo-use-as-image" />
            Use this image as the recipe photo
          </label>
        </div>
      )}

      <p className="text-[10px] italic text-gray-500 leading-snug flex items-center gap-1">
        <Edit3 className="w-3 h-3" strokeWidth={1.5} /> All fields above remain editable in the recipe card. Save only happens when you press Save recipe.
      </p>
    </div>
  );
}

const INPUT_CLS = "w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-300";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default RecipePhotoImportModal;
